import {
  ApiError,
  apiBase,
  getToken,
  normalizeApiErrorMessage,
  type MailListResponse,
  type MailThread,
} from "@/lib/api";

export const GMAIL_LIST_RATE_LIMIT_BANNER =
  "Gmail is rate limiting older mail. Retrying...";

export type MailListFetchResult = {
  payload: MailListResponse | MailThread[];
  status: number;
};

export function mailListPageError(
  payload: MailListResponse | MailThread[],
): MailListResponse["pageError"] | undefined {
  if (Array.isArray(payload)) return undefined;
  return payload.pageError;
}

export function isGmailListRateLimit(pageError?: MailListResponse["pageError"]): boolean {
  return pageError?.code === "gmail_rate_limit";
}

export function listRateLimitRetrySec(
  pageError?: MailListResponse["pageError"],
  status?: number,
): number {
  if (pageError?.retryAfterSec && pageError.retryAfterSec > 0) return pageError.retryAfterSec;
  if (status === 429) return 60;
  return pageError ? 60 : 0;
}

/** GET /api/mail — keeps JSON body on HTTP 429 (Gmail list quota) instead of throwing. */
export async function fetchMailList(path: string, timeoutMs: number): Promise<MailListFetchResult> {
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set("authorization", `Bearer ${token}`);

  const controller = timeoutMs > 0 ? new AbortController() : null;
  const timer =
    controller && timeoutMs > 0
      ? window.setTimeout(() => controller.abort(), timeoutMs)
      : null;

  try {
    const response = await fetch(`${apiBase()}${path}`, {
      headers,
      signal: controller?.signal,
    });
    const text = await response.text();
    let body: Record<string, unknown> = {};
    try {
      body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
    } catch {
      body = {
        message: normalizeApiErrorMessage(response.status, text || "Invalid response"),
      };
    }

    if (response.status === 401) {
      throw new ApiError(
        401,
        normalizeApiErrorMessage(401, String(body.message || "Sign in to continue.")),
      );
    }
    if (response.status === 429) {
      return { payload: body as MailListResponse | MailThread[], status: 429 };
    }
    if (!response.ok) {
      throw new ApiError(
        response.status,
        normalizeApiErrorMessage(
          response.status,
          String(body.message || "Something went wrong."),
        ),
        body.details,
      );
    }
    return { payload: body as MailListResponse | MailThread[], status: response.status };
  } finally {
    if (timer != null) window.clearTimeout(timer);
  }
}
